import { Text } from '@shared/components';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import { ReactElement, useState } from 'react';
import { Button, ButtonProps } from '../button';
import Checkbox from '../checkbox/Checkbox';
import Popover from '../popover/Popover';

const ROOT = makeRootClassName('PopoverMultiSelect');
const el = makeElementClassNameFactory(ROOT);

export type PopoverMultiSelectOptionData = {
  id: string;
  label: string;
  selected: boolean;
};

type PopoverMultiSelectProps = {
  options: PopoverMultiSelectOptionData[];
  onOptionChange: (id: string, newValue: boolean) => void;
  buttonLabel: string;
  buttonProps?: ButtonProps;
  onOpen?: () => void;
  closeOnOptionChange?: boolean;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
};

export function PopoverMultiSelect(p: PopoverMultiSelectProps): ReactElement {
  const [open, setOpen] = useState(false);
  const hasBulkControls = !!(p.onSelectAll || p.onDeselectAll);
  return (
    <div className={ROOT}>
      <Popover
        roundedLarge
        open={open}
        onOpenChange={(val) => {
          setOpen(val);
          if (val && p.onOpen) {
            p.onOpen();
          }
        }}
        content={
          <div className={el`container`}>
            {hasBulkControls && (
              <BulkControls
                onSelectAll={p.onSelectAll}
                onDeselectAll={p.onDeselectAll}
              />
            )}
            {p.options.map((option) => (
              <CheckboxRow
                key={option.id}
                label={option.label}
                value={option.selected}
                onCheckedChange={(value) => {
                  p.onOptionChange(option.id, value);
                  if (p.closeOnOptionChange) setOpen(false);
                }}
              />
            ))}
          </div>
        }
      >
        <Button {...p.buttonProps}>{p.buttonLabel}</Button>
      </Popover>
    </div>
  );
}

type BulkControlsProps = {
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
};

function BulkControls({ onSelectAll, onDeselectAll }: BulkControlsProps) {
  return (
    <div className={el`bulk-controls`}>
      {onSelectAll && (
        <button type="button" className={el`bulk-action`} onClick={onSelectAll}>
          <Text type="body-xs">Select all</Text>
        </button>
      )}
      {onDeselectAll && (
        <button
          type="button"
          className={el`bulk-action`}
          onClick={onDeselectAll}
        >
          <Text type="body-xs">Deselect all</Text>
        </button>
      )}
    </div>
  );
}

type CheckboxRowProps = {
  label: string;
  value: boolean;
  onCheckedChange: (value: boolean) => void;
};

function CheckboxRow(props: CheckboxRowProps) {
  const p = { ...props };
  const { label, value } = p;
  const isDisabled = false;

  return (
    <div className={clsx(el`checkbox-row`, { 'is-checked': value })}>
      <Checkbox
        disabled={isDisabled}
        checked={value}
        onCheckedChange={p.onCheckedChange}
      />
      <Text type="body-sm">{label}</Text>
    </div>
  );
}
